'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const talents = [
  {
    id: 'singing',
    label: 'Singer',
    icon: 'MusicalNoteIcon' as const,
    headline: 'Hindustani Classical & Fusion',
    description:
      "Trained in Hindustani classical vocals, Kavita's voice traverses ragas at dawn and fusion compositions at dusk. She has performed at NCPA Pune, Prithvi Theatre, and international stages in London and Singapore.",
    image: 'https://images.unsplash.com/photo-1689794387216-a195bad7993c',
    imageAlt:
      'Kavita Thakur singing on stage with microphone, warm stage lighting creating a dramatic silhouette',
    color: '#B8966E',
    highlight: '12+ years performing',
    size: 'tall',
  },
  {
    id: 'writing',
    label: 'Writer',
    icon: 'PencilSquareIcon' as const,
    headline: 'Poetry, Essays & Literary Fiction',
    description:
      'Three published collections that bridge the personal and the philosophical. Her writing has appeared in The Hindu, Scroll.in, and literary journals across South Asia.',
    image: 'https://images.unsplash.com/photo-1620810015726-6d647d32696b',
    imageAlt:
      'Open journal with handwritten poetry on a wooden desk with ink pen and dried flowers',
    color: '#C9A99A',
    highlight: '3 published books',
    size: 'wide',
  },
  {
    id: 'instrument',
    label: 'Musician',
    icon: 'SpeakerWaveIcon' as const,
    headline: 'Sitar, Harmonium & Guitar',
    description:
      'A multi-instrumentalist rooted in classical training with an ear for contemporary soundscapes.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_136b2269a-1772834288046.png',
    imageAlt: 'Close-up of sitar strings being played with skilled fingers in warm ambient light',
    color: '#8B9E8A',
    highlight: '3 instruments',
    size: 'small',
  },
  {
    id: 'coaching',
    label: 'Coach',
    icon: 'SparklesIcon' as const,
    headline: 'Personality Development',
    description:
      "500+ clients transformed. Kavita's coaching framework — 'The Voice Method' — blends performance psychology, communication science, and artistic practice to help you command any room.",
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17b917ddb-1772834278846.png',
    imageAlt: 'Kavita Thakur coaching a small group workshop, participants engaged in discussion',
    color: '#B8966E',
    highlight: '500+ clients',
    size: 'wide',
  },
];

export default function TalentsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.will-animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="talents"
      className="py-32 lg:py-40"
      ref={sectionRef}
      style={{ background: 'linear-gradient(180deg, #FAF6F0 0%, #F2E8DF 50%, #FAF6F0 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="scroll-reveal will-animate">
            <span className="section-label">Disciplines</span>
            <h2
              className="font-fraunces font-light leading-tight mt-3"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
            >
              Four ways to <br />
              <em className="italic">move the world.</em>
            </h2>
          </div>
          <p className="text-muted text-sm leading-relaxed max-w-sm scroll-reveal will-animate">
            Each discipline feeds the others. Music sharpens the writing. Writing deepens the
            coaching. Coaching unlocks the music.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
          {/* Card 1 — Tall (spans 2 rows) */}
          <div
            className="bento-card scroll-reveal will-animate lg:col-span-4 lg:row-span-2 group"
            style={{ background: 'var(--charcoal)', minHeight: '520px' }}
          >
            <div className="relative w-full h-full min-h-[520px] overflow-hidden rounded-[28px]">
              <AppImage
                src={talents[0].image}
                alt={talents[0].imageAlt}
                fill
                className="object-cover transition-transform duration-1000 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 400px"
              />

              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(28,25,23,0.85) 0%, rgba(28,25,23,0.2) 60%, transparent 100%)',
                }}
              />

              <div className="absolute inset-0 p-8 flex flex-col justify-between">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'rgba(184,150,110,0.25)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(184,150,110,0.3)',
                  }}
                >
                  <Icon
                    name={talents[0].icon}
                    size={22}
                    className="text-gold-light"
                    variant="solid"
                  />
                </div>
                <div>
                  <span className="text-10 tracking-widest uppercase text-gold font-bold">
                    {talents[0].label}
                  </span>
                  <h3 className="font-fraunces text-2xl text-cream font-light mt-2 leading-snug">
                    {talents[0].headline}
                  </h3>
                  <p className="text-cream/60 text-sm mt-3 leading-relaxed">
                    {talents[0].description}
                  </p>
                  <div
                    className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{
                      background: 'rgba(184,150,110,0.2)',
                      border: '1px solid rgba(184,150,110,0.3)',
                    }}
                  >
                    <span className="text-10 text-gold tracking-wider">{talents[0].highlight}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 — Wide */}
          <div
            className="bento-card scroll-reveal will-animate lg:col-span-8 group"
            style={{ background: '#F7F0E8', minHeight: '240px' }}
          >
            <div className="relative w-full h-full min-h-[240px] overflow-hidden rounded-[28px] flex flex-col lg:flex-row">
              <div className="flex-1 p-8 flex flex-col justify-between">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'rgba(201,169,154,0.2)',
                    border: '1px solid rgba(201,169,154,0.3)',
                  }}
                >
                  <Icon name={talents[1].icon} size={22} className="text-rose" variant="solid" />
                </div>
                <div>
                  <span
                    className="text-10 tracking-widest uppercase font-bold"
                    style={{ color: 'var(--rose)' }}
                  >
                    {talents[1].label}
                  </span>
                  <h3 className="font-fraunces text-2xl text-charcoal font-light mt-2 leading-snug">
                    {talents[1].headline}
                  </h3>
                  <p className="text-muted text-sm mt-2 leading-relaxed">
                    {talents[1].description}
                  </p>
                  <div
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{
                      background: 'rgba(201,169,154,0.15)',
                      border: '1px solid rgba(201,169,154,0.25)',
                    }}
                  >
                    <span
                      className="text-10 font-bold tracking-wider"
                      style={{ color: 'var(--rose)' }}
                    >
                      {talents[1].highlight}
                    </span>
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-52 h-48 lg:h-full overflow-hidden flex-shrink-0">
                <AppImage
                  src={talents[1].image}
                  alt={talents[1].imageAlt}
                  fill
                  className="object-cover transition-transform duration-1000 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 220px"
                />
              </div>
            </div>
          </div>

          {/* Card 3 — Small */}
          <div
            className="bento-card scroll-reveal will-animate lg:col-span-3 group"
            style={{ background: '#EEF2ED', minHeight: '240px' }}
          >
            <div className="relative h-full min-h-[240px] overflow-hidden rounded-[28px]">
              <AppImage
                src={talents[2].image}
                alt={talents[2].imageAlt}
                fill
                className="object-cover transition-transform duration-1000 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 300px"
              />

              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(28,25,23,0.75) 0%, transparent 60%)',
                }}
              />

              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(139,158,138,0.3)', backdropFilter: 'blur(8px)' }}
                >
                  <Icon name={talents[2].icon} size={18} className="text-white" variant="solid" />
                </div>
                <div>
                  <span className="text-10 tracking-widest uppercase text-sage font-bold">
                    {talents[2].label}
                  </span>
                  <h3 className="font-fraunces text-lg text-cream font-light mt-1">
                    {talents[2].headline}
                  </h3>
                  <p className="text-10 tracking-wider text-cream/60 mt-1">
                    {talents[2].highlight}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4 — Wide */}
          <div
            className="bento-card scroll-reveal will-animate lg:col-span-5 group"
            style={{ background: 'var(--charcoal)', minHeight: '240px' }}
          >
            <div className="relative h-full min-h-[240px] overflow-hidden rounded-[28px] flex flex-col lg:flex-row">
              <div className="w-full lg:w-44 h-48 lg:h-full overflow-hidden flex-shrink-0">
                <AppImage
                  src={talents[3].image}
                  alt={talents[3].imageAlt}
                  fill
                  className="object-cover transition-transform duration-1000 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 200px"
                />
              </div>
              <div className="flex-1 p-6 flex flex-col justify-between">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'rgba(184,150,110,0.25)',
                    border: '1px solid rgba(184,150,110,0.3)',
                  }}
                >
                  <Icon
                    name={talents[3].icon}
                    size={18}
                    className="text-gold-light"
                    variant="solid"
                  />
                </div>
                <div>
                  <span className="text-10 tracking-widest uppercase text-gold font-bold">
                    {talents[3].label}
                  </span>
                  <h3 className="font-fraunces text-xl text-cream font-light mt-2 leading-snug">
                    {talents[3].headline}
                  </h3>
                  <p className="text-cream/50 text-xs mt-2 leading-relaxed line-clamp-3">
                    {talents[3].description}
                  </p>
                  <div
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                    style={{
                      background: 'rgba(184,150,110,0.2)',
                      border: '1px solid rgba(184,150,110,0.3)',
                    }}
                  >
                    <span className="text-10 text-gold tracking-wider">{talents[3].highlight}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
